import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Switch, OutlinedInput, IconButton
} from '@mui/material';
import { jobRoleService, skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Close as CloseIcon } from '@mui/icons-material';

const JobRoleForm = ({ open, onClose, onSubmit, jobRole = null }) => {
  const [formData, setFormData] = useState({
    role_name: '',
    // skill_category_id: '',
    skill_category_ids: [],
    status: 1
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingJobRole, setLoadingJobRole] = useState(false);
  const { showToast } = useToast();

  const isEdit = Boolean(jobRole);

  useEffect(() => {
    if (open) {
      fetchCategories();
      if (isEdit && jobRole) {
        fetchJobRoleData(jobRole.id);
        // setFormData({
        //   role_name: jobRole.role_name || '',
        //   skill_category_id: jobRole.skill_category_id || '',
        //   status: jobRole.status || 1
        // });
      } else {
        setFormData({
          role_name: '',
          // skill_category_id: '',
          skill_category_ids: [],
          status: 1
        });
      }
      setErrors({});
    }
  }, [open, jobRole, isEdit]);

  const fetchCategories = async () => {
    try {
      const response = await skillService.getCategories();
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showToast('Failed to load categories', 'error');
    }
  };

  const fetchJobRoleData = async (jobRoleId) => {
    setLoadingJobRole(true);
    try {
      const response = await jobRoleService.getJobRoleById(jobRoleId);
      if (response.data.success) {
        const jobRoleData = response.data.jobRole;
        const ids = jobRoleData.skill_category_id
          ? jobRoleData.skill_category_id.split(',').map((id) => Number(id))
          : [];


        setFormData({
          role_name: jobRoleData.role_name || '',
          // skill_category_id: jobRoleData.skill_category_id || '',
          // skill_category_ids: jobRoleData.skill_category_ids || [],
          skill_category_ids: ids,
          status: jobRoleData.status || 1
        });
      } else {
        showToast('Failed to load job role data', 'error');
      }
    } catch (error) {
      console.error('Error fetching job role:', error);
      showToast('Failed to load job role data', 'error');
    } finally {
      setLoadingJobRole(false);
    }
  };
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.role_name.trim()) {
      newErrors.role_name = 'Role name is required';
    } else if (formData.role_name.trim().length < 2) {
      newErrors.role_name = 'Role name must be at least 2 characters';
    }

    if (!formData.skill_category_ids || formData.skill_category_ids.length === 0) {
      newErrors.skill_category_ids = 'At least one skill category is required';
    }

    console.log('formData.skill_category_ids:', formData.skill_category_ids);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        role_name: formData.role_name.trim(),
        skill_category_ids: formData.skill_category_ids,
        status: formData.status ? 1 : 0
      };

      let response;
      if (isEdit) {
        response = await jobRoleService.updateJobRole(jobRole.id, submitData);
      } else {
        response = await jobRoleService.createJobRole(submitData);
      }

      if (response.data.success) {
        showToast(response.data.message, 'success');
        onSubmit();
        onClose();
      } else {
        showToast(response.data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving job role:', error);
      if (error.response?.data?.message) {
        showToast(error.response.data.message, 'error');
      } else {
        showToast('Failed to save job role', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEdit ? 'Edit Job Role' : 'Create New Job Role'}
          </Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              name="role_name"
              label="Role Name"
              value={formData.role_name}
              onChange={handleChange}
              error={!!errors.role_name}
              helperText={errors.role_name}
              fullWidth
              required
              disabled={loading}
              placeholder="Enter job role name"
            />
            <FormControl fullWidth error={!!errors.skill_category_ids} size="small" required>
              <InputLabel>Skill Categories</InputLabel>
              <Select
                name="skill_category_ids"
                multiple
                value={formData.skill_category_ids}
                onChange={(e) => {
                  const { value } = e.target;
                  setFormData(prev => ({
                    ...prev,
                    skill_category_ids: typeof value === 'string' ? value.split(',') : value,
                  }));
                }}
                input={<OutlinedInput label="Skill Categories" />}
                renderValue={(selected) => {
                  return selected
                    .map((id) => {
                      const category = categories.find((cat) => cat.id === id);
                      return category ? category.category_name : '';
                    })
                    .join(', ');
                }}
                disabled={loading}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.category_name}
                  </MenuItem>

                ))}
              </Select>
              {errors.skill_category_ids && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.skill_category_ids}
                </Typography>
              )}
            </FormControl>


            <FormControlLabel
              control={
                <Switch
                  name="status"
                  checked={formData.status === 1}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    status: e.target.checked ? 1 : 0
                  }))}
                  disabled={loading}
                />
              }
              label="Active Status"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Job Role' : 'Create Job Role')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default JobRoleForm;